// Shell de la app: barra lateral de navegación + topbar (búsqueda, notificaciones,
// usuario). Envuelve todas las rutas privadas.
import type { JSX } from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BarChart3, Bell, Building2, CalendarCheck, Contact, FileText, Handshake,
  LayoutDashboard, LogOut, Megaphone, type LucideIcon, Moon, Package, PanelLeftClose, PanelLeftOpen,
  ChevronDown, Search, Settings, Sun, Target, UserCog, Users,
} from "lucide-react";
import { getCountry, setCountry, type CountrySel } from "../lib/country.ts";
import { CountryFlag } from "./CountryFlag.tsx";
import { api } from "../lib/api.ts";
import { useLogout, useMe } from "../lib/auth.ts";
import { initials } from "../lib/format.ts";
import { useTheme } from "../lib/theme.ts";
import { Toaster } from "./toast.tsx";
import { CommandPalette } from "./CommandPalette.tsx";

const NAV: Array<{ to: string; label: string; icon: LucideIcon }> = [
  { to: "/", label: "Inicio", icon: LayoutDashboard },
  { to: "/leads", label: "Leads", icon: Target },
  { to: "/contacts", label: "Contactos", icon: Users },
  { to: "/accounts", label: "Cuentas", icon: Building2 },
  { to: "/terceros", label: "Terceros", icon: Contact },
  { to: "/deals", label: "Negocios", icon: Handshake },
  { to: "/activities", label: "Actividades", icon: CalendarCheck },
  { to: "/products", label: "Servicios", icon: Package },
  { to: "/quotes", label: "Cotizaciones", icon: FileText },
  { to: "/marketing", label: "Marketing", icon: Megaphone },
  { to: "/reports", label: "Reportes", icon: BarChart3 },
  { to: "/users", label: "Usuarios", icon: UserCog },
  { to: "/settings", label: "Configuración", icon: Settings },
];

export function Layout(): JSX.Element {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("tiger.sidebar") === "1");
  const toggle = (): void => setCollapsed((v) => { localStorage.setItem("tiger.sidebar", v ? "0" : "1"); return !v; });
  return (
    <div className="min-h-screen flex">
      <Sidebar collapsed={collapsed} onToggle={toggle} />
      <div className="flex-1 flex flex-col min-w-0">
        <Topbar />
        <main className="flex-1 min-w-0 p-5">
          <Outlet />
        </main>
      </div>
      <Toaster />
      <CommandPalette />
    </div>
  );
}

function Sidebar({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }): JSX.Element {
  const location = useLocation();
  const itemRefs = useRef<Array<HTMLAnchorElement | null>>([]);
  const [ind, setInd] = useState<{ top: number; height: number; show: boolean }>({ top: 0, height: 0, show: false });

  const activeIndex = NAV.reduce((best, n, i) => {
    const p = location.pathname;
    const match = n.to === "/" ? p === "/" : (p === n.to || p.startsWith(n.to + "/"));
    return match ? i : best;
  }, -1);

  // Indicador deslizante: mide el ítem activo y desliza la barra a su posición.
  useLayoutEffect(() => {
    const el = activeIndex >= 0 ? itemRefs.current[activeIndex] : null;
    if (el) setInd({ top: el.offsetTop, height: el.offsetHeight, show: true });
    else setInd((s) => ({ ...s, show: false }));
  }, [activeIndex, collapsed]);

  return (
    <aside
      className="barra-lateral shrink-0 sticky top-0 h-screen self-start flex flex-col transition-[width] duration-300 ease-in-out-quart"
      style={{ width: collapsed ? 60 : 196 }}
    >
      <div className="barra-oscura h-14 shrink-0 flex items-center gap-2.5 px-3 overflow-hidden">
        <img src="/tigre.jpg" alt="Tiger" className="w-9 h-9 rounded-lg object-cover shrink-0" />
        <span className={`font-semibold text-base text-ink whitespace-nowrap transition-opacity duration-150 ${collapsed ? "opacity-0" : "opacity-100"}`}>Tiger</span>
      </div>
      <nav className="relative flex-1 p-2 space-y-0.5 overflow-y-auto overflow-x-hidden scrollbar-thin border-r border-line">
        <span className="absolute left-2 right-2 rounded-md bg-primary-soft transition-all duration-250 ease-in-out-quart pointer-events-none z-0"
          style={{ top: ind.top, height: ind.height, opacity: ind.show ? 1 : 0 }} />
        <span className="absolute left-0 w-[3px] rounded-r bg-[var(--primary-text)] transition-all duration-250 ease-in-out-quart pointer-events-none z-0"
          style={{ top: ind.top + 6, height: Math.max(0, ind.height - 12), opacity: ind.show ? 1 : 0 }} />
        {NAV.map((n, i) => {
          const Icon = n.icon;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              ref={(el) => { itemRefs.current[i] = el; }}
              title={collapsed ? n.label : undefined}
              onClick={(e) => e.currentTarget.blur()}
              className={({ isActive }) =>
                `group relative z-[1] flex items-center gap-2.5 px-2.5 py-[7px] rounded-lg text-[13px] transition-colors ${
                  isActive ? "text-primary font-medium" : "text-ink-secondary hover:text-ink"
                } ${collapsed ? "justify-center" : ""}`
              }
            >
              <Icon size={18} strokeWidth={1.75} className="shrink-0 transition-transform duration-150 ease-out-expo group-hover:scale-[1.08]" />
              <span className={`whitespace-nowrap transition-opacity duration-150 ${collapsed ? "opacity-0 w-0 overflow-hidden" : "opacity-100"}`}>{n.label}</span>
            </NavLink>
          );
        })}
        {/* Colapsar: justo debajo del último ítem (Configuración) */}
        <button onClick={(e) => { onToggle(); e.currentTarget.blur(); }} className={`w-full mt-0.5 p-2 rounded-lg text-ink-muted hover:bg-surface-hover hover:text-ink transition-colors flex items-center gap-2.5 text-sm ${collapsed ? "justify-center" : ""}`} title={collapsed ? "Expandir" : "Colapsar"}>
          {collapsed ? <PanelLeftOpen size={18} strokeWidth={1.75} /> : <><PanelLeftClose size={18} strokeWidth={1.75} className="shrink-0" /> <span>Colapsar</span></>}
        </button>
      </nav>
    </aside>
  );
}

// Selector de país (banderas) — solo para usuarios autorizados en AMBOS países.
// Cambia el país activo (header X-Country) y refresca todos los datos.
const COUNTRY_OPTS: Array<{ v: CountrySel; label: string }> = [
  { v: "ALL", label: "Ambos" },
  { v: "CO", label: "Colombia" },
  { v: "PE", label: "Perú" },
];
function CountryGlyph({ v }: { v: CountrySel }): JSX.Element {
  if (v === "ALL") {
    return (
      <span className="flex items-center -space-x-1">
        <CountryFlag country="CO" className="w-4 h-3" />
        <CountryFlag country="PE" className="w-4 h-3" />
      </span>
    );
  }
  return <CountryFlag country={v} />;
}
function CountrySwitcher(): JSX.Element | null {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState<CountrySel>(getCountry());
  if (!(me?.countries?.co && me?.countries?.pe)) return null;
  const cur = COUNTRY_OPTS.find((o) => o.v === sel) ?? COUNTRY_OPTS[0]!;
  const choose = (v: CountrySel): void => {
    setSel(v); setCountry(v); setOpen(false);
    void qc.invalidateQueries();
  };
  return (
    <div className="relative shrink-0">
      <button
        onClick={() => setOpen((o) => !o)}
        title="País activo"
        className="flex items-center gap-2 h-9 px-3 rounded-md border border-line bg-canvas hover:bg-surface-hover text-sm text-ink-secondary transition-colors"
      >
        <CountryGlyph v={cur.v} />
        <span className="hidden sm:block">{cur.label}</span>
        <ChevronDown size={14} strokeWidth={2} />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-40 card p-1 z-30" onMouseLeave={() => setOpen(false)}>
          {COUNTRY_OPTS.map((o) => (
            <button
              key={o.v}
              onClick={() => choose(o.v)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-surface-hover ${o.v === sel ? "text-primary font-medium" : "text-ink-secondary"}`}
            >
              <CountryGlyph v={o.v} />{o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Topbar(): JSX.Element {
  const { data: me } = useMe();
  const logout = useLogout();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);
  const [theme, toggleTheme] = useTheme();

  const openCmdk = (): void => { window.dispatchEvent(new Event("tiger:cmdk")); };

  const notif = useQuery<{ rows: Array<{ id: string; isRead: boolean; title: string; body: string }> }>({
    queryKey: ["notifications"],
    queryFn: () => api.get("/notifications"),
    refetchInterval: 60_000,
  });
  const unread = (notif.data?.rows ?? []).filter((n) => !n.isRead).length;

  return (
    <header className="barra-oscura h-14 shrink-0 sticky top-0 z-30 flex items-center gap-3 px-5">
      <NotificationsBell count={unread} items={notif.data?.rows ?? []} />
      <button
        onClick={openCmdk}
        className="flex items-center gap-2 h-9 px-3 rounded-md border border-line bg-canvas hover:bg-surface-hover text-ink-muted transition-colors text-sm min-w-[180px]"
      >
        <Search size={16} strokeWidth={1.75} />
        <span className="flex-1 text-left">Buscar…</span>
        <kbd className="text-[10px] border border-line rounded px-1 py-0.5 bg-surface">⌘K</kbd>
      </button>
      <div className="flex-1" />
      <CountrySwitcher />
      <button type="button" className="icono-btn" onClick={toggleTheme} title={theme === "dark" ? "Cambiar a modo claro" : "Cambiar a modo oscuro"} aria-label={theme === "dark" ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}>
        {theme === "dark" ? <Sun size={19} strokeWidth={1.8} /> : <Moon size={19} strokeWidth={1.8} />}
      </button>
      <a href="https://sacsconsultores.com/" target="_blank" rel="noreferrer" title="SACS Consultores" className="shrink-0">
        <img src="/logo-sacs.jpg" alt="SACS Consultores" className="h-8 w-auto rounded object-contain hover:opacity-80 transition-opacity" />
      </a>
      <div className="relative">
        <button
          className="flex items-center gap-2 rounded-full hover:bg-surface-hover p-1 pr-2 transition-colors"
          onClick={() => setMenu((v) => !v)}
        >
          <span className="w-8 h-8 rounded-full bg-primary text-white grid place-items-center text-xs font-semibold">
            {initials(me?.firstName, me?.lastName)}
          </span>
          <span className="text-sm text-ink-secondary hidden sm:block">{me?.firstName} {me?.lastName}</span>
        </button>
        {menu && (
          <div className="absolute right-0 mt-1 w-52 card p-1 z-30" onMouseLeave={() => setMenu(false)}>
            <div className="px-3 py-2 text-xs text-ink-muted border-b border-line">{me?.email}</div>
            <button
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-ink-secondary hover:bg-surface-hover rounded"
              onClick={() => { setMenu(false); navigate("/settings"); }}
            >
              <Settings size={16} strokeWidth={1.75} /> Configuración
            </button>
            <button
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-danger hover:bg-danger-soft rounded"
              onClick={() => logout.mutate(undefined, { onSuccess: () => navigate("/login") })}
            >
              <LogOut size={16} strokeWidth={1.75} /> Cerrar sesión
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

function NotificationsBell(
  { count, items }: { count: number; items: Array<{ id: string; title: string; body: string; isRead: boolean }> },
): JSX.Element {
  const [open, setOpen] = useState(false);
  const [ring, setRing] = useState(false);
  const prev = useRef(count);
  // "Ring" cuando llega una notificación nueva (el contador sube).
  useEffect(() => {
    if (count > prev.current) { setRing(true); const t = setTimeout(() => setRing(false), 650); prev.current = count; return () => clearTimeout(t); }
    prev.current = count;
    return undefined;
  }, [count]);
  return (
    <div className="relative">
      <button className="btn-ghost btn-sm relative" onClick={() => setOpen((v) => !v)} aria-label="Notificaciones">
        <Bell size={20} strokeWidth={1.75} className={ring ? "animate-bell" : ""} />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-danger text-white text-[10px] rounded-full min-w-4 h-4 px-1 grid place-items-center">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-80 card z-30 overflow-hidden" onMouseLeave={() => setOpen(false)}>
          <div className="px-4 py-2.5 border-b border-line font-medium text-sm">Notificaciones</div>
          <div className="max-h-96 overflow-y-auto scrollbar-thin">
            {items.length === 0 ? (
              <div className="px-4 py-6 text-sm text-ink-muted text-center">Sin notificaciones.</div>
            ) : (
              items.slice(0, 15).map((n) => (
                <div key={n.id} className={`px-4 py-2.5 border-b border-line ${n.isRead ? "" : "bg-primary-soft"}`}>
                  <div className="text-sm font-medium text-ink">{n.title}</div>
                  <div className="text-xs text-ink-secondary">{n.body}</div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
